import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../utils/supabase';
import { filterProfileByPrivacy, canViewProfile } from '../utils/privacyUtils';
import { useAuth } from '../hooks/useAuth';
import { useSubscriptionLimits } from '../hooks/useSubscriptionLimits';
import Icon from '../components/AppIcon';
import ProfileViewsService from '../services/profileViewsService';
import aiInterviewService from '../services/aiInterviewService';
import ProfileHero from './va-profile/components/ProfileHero';
import ProfileHeroSidebar from './va-profile/components/ProfileHeroSidebar';
import AboutSection from './va-profile/components/AboutSection';
import VideoIntroductionSection from './va-profile/components/VideoIntroductionSection';
import SkillsSection from './va-profile/components/SkillsSection';
import ExperienceTimeline from './va-profile/components/ExperienceTimeline';
import PortfolioGallery from './va-profile/components/PortfolioGallery';
import WorkStyleSection from './va-profile/components/WorkStyleSection';
import ReviewsSection from './va-profile/components/ReviewsSection';
import ProfileSEO from './va-profile/components/ProfileSEO';
import AIVideoInterviewSection from './va-profile/components/AIVideoInterviewSection';

// UI Components
import Toast from '../components/ui/Toast';
import PrivacySectionWrapper from '../components/privacy/PrivacySectionWrapper';

const PublicVAProfile = () => {
  const { username } = useParams();
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const { getCurrentPlan } = useSubscriptionLimits();
  
  // username could be either a public_username or an ID
  
  // State management
  const [profileData, setProfileData] = useState(null);
  const [originalProfileData, setOriginalProfileData] = useState(null);
  const [portfolioItems, setPortfolioItems] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [skillAssessments, setSkillAssessments] = useState({
    userAssessments: [],
    aiAssessments: [],
    skillCategories: [],
    skills: []
  });
  const [aiAssessments, setAiAssessments] = useState(new Map());
  const [aiInterview, setAiInterview]     = useState(null);
  
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  // Function to fetch public profile data
  const fetchPublicProfile = async () => {
    if (!username) {
      setError('No username provided');
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      
      // Check if username is a UUID (ID) or a public_username
      const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(username);
        
        // Fetch public profile data (limited fields for privacy)
        // Note: Changed from va_profile_stats to user_profiles table to access individual profile fields
        let query = supabase
          .from('user_profiles')
          .select(`
            id,
            full_name,
            bio,
            skills,
            languages,
            certifications,
            soft_skills,
            education,
            experience_years,
            experience_level,
            location,
            phone,
            linkedin,
            website,
            github_url,
            hourly_rate,
            minimum_rate,
            maximum_rate,
            preferred_currency,
            profile_picture,
            portfolio_url,
            portfolio_links,
            portfolio_description,
            work_samples,
            public_username,
            title,
            availability_status,
            availability_hours,
            timezone,
            work_experience,
            work_pace,
            feedback_preference,
            collaboration_style,
            communication_style,
            problem_solving_approach,
            work_style_description,
            preferred_project_types,
            years_of_experience_category,
            video_introduction_url,
            industry_specialization,
            identity_verification_status,
            created_at,
            rating,
            total_reviews
          `);
          
        // Use either ID or public_username based on the parameter format
        if (isUuid) {
          query = query.eq('id', username);
        } else {
          query = query.eq('public_username', username);
        }
        
        const { data: profile, error: profileError } = await query.maybeSingle();

        if (profileError) {
          if (process.env.NODE_ENV === 'development') {
            console.error('[PublicVAProfile] Profile fetch error:', {
              message: profileError.message,
              code: profileError.code,
              details: profileError.details,
              hint: profileError.hint
            });
          }
          throw profileError;
        }
        if (!profile) throw new Error('Profile not found');

        // Check moderation status - block access to suspended/banned profiles
        const { data: moderationStatus } = await supabase
          .from('user_moderation_status')
          .select('status, reason, expires_at')
          .eq('user_id', profile.id)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        // If user is suspended or banned, throw error to show unavailable message
        if (moderationStatus && (moderationStatus.status === 'suspended' || moderationStatus.status === 'banned')) {
          throw new Error('Profile not available');
        }

        // Store original profile data
        const currentUserId = user?.id;
        const isOwnProfile = currentUserId === profile.id;
        setOriginalProfileData(profile);
        
        // For public VA profile stats, use the data as-is (all profiles from this view are public VAs)
        const filteredProfile = profile;

        // Fetch public portfolio items (only if portfolio is visible)
        let portfolio = [];
        const portfolioVisible = filteredProfile.portfolio_url !== undefined;
        
        if (portfolioVisible) {
          const { data: portfolioData, error: portfolioError } = await supabase
            .from('portfolio_items')
            .select('*')
            .eq('user_id', profile.id)
            .eq('is_public', true)
            .order('created_at', { ascending: false });

          if (portfolioError) throw portfolioError;
          portfolio = portfolioData || [];
        }

        // Fetch skill assessments (only if skills are visible)
        // Strategy: Fetch both legacy assessments for SkillsSection and AI assessments for ProfileHero
        // AI assessments are filtered by score >= 70 (only verified skills) for public display
        let assessments = [];
        let aiAssessments = [];
        const skillsVisible = filteredProfile.skills !== undefined;
        
        if (skillsVisible) {
          // Fetch legacy skill assessments
          const { data: assessmentsData, error: assessmentsError } = await supabase
            .from('user_skill_assessments')
            .select(`
              id,
              score,
              max_score,
              passed,
              completed_at,
              skills(
                id,
                name,
                difficulty_level,
                skill_categories(name, icon)
              )
            `)
            .eq('user_id', profile.id)
            .order('completed_at', { ascending: false });

          if (assessmentsError) throw assessmentsError;
          assessments = assessmentsData || [];

          // Fetch AI skill assessments (only passed assessments with score >= 70)
          const { data: aiAssessmentData, error: aiError } = await supabase
            .from('ai_skill_assessment_sessions')
            .select('*')
            .eq('user_id', profile.id)
            .gte('final_score', 70)
            .order('updated_at', { ascending: false });

          if (aiError) throw aiError;
          aiAssessments = aiAssessmentData || [];
        }

        // Fetch public reviews
        const { data: reviewsData, error: reviewsError } = await supabase
          .from('reviews')
          .select(`
            id,
            rating,
            content,
            created_at,
            user_profiles!employer_id (
              full_name,
              profile_picture,
              company_name
            )
          `)
          .eq('va_id', profile.id)
          .eq('is_public', true)
          .order('created_at', { ascending: false });

        if (reviewsError) throw reviewsError;

        // Format data for components using filtered profile
        const formattedProfile = {
          ...filteredProfile,
          portfolio_items: portfolio || [],
          // Add computed metrics (total_reviews from user_profiles table)
          clientCount: 0, // Calculated from completed contracts/jobs in future enhancement
          completedJobs: 0, // Calculated from completed contracts in future enhancement
          reviewCount: filteredProfile.total_reviews || 0,
          workExperienceCount: (filteredProfile.work_experience?.length) || 0,
          responseTime: '24h' // Default value
        };

        const formattedReviews = (reviewsData || []).map(review => ({
          id: review.id,
          rating: review.rating,
          content: review.content,
          date: review.created_at,
          clientName: review.user_profiles?.full_name || 'Anonymous Client',
          clientAvatar: review.user_profiles?.profile_picture || '',
          clientTitle: review.user_profiles?.company_name || 'Client',
          projectType: 'Project', // Default since project_type field doesn't exist
          isVerified: true,
          helpfulCount: 0, // Default since helpful_count field doesn't exist
          metrics: {
            quality: review.rating,
            communication: review.rating,
            timeliness: review.rating,
            value: review.rating
          },
          projectDetails: null // Field doesn't exist
        }));

        setProfileData(formattedProfile);
        setPortfolioItems(portfolio || []);
        setReviews(formattedReviews);
        setSkillAssessments({
          userAssessments: assessments || [],
          aiAssessments: aiAssessments || [],
          skillCategories: [],
          skills: []
        });
        
        // Create AI assessments Map for ProfileHero (O(1) skill lookup)
        // Only the latest assessment per skill is kept (skills can have multiple attempts)
        const aiAssessmentMap = new Map();
        if (aiAssessments && aiAssessments.length > 0) {
          aiAssessments.forEach(assessment => {
            // Store only the first/latest assessment for each skill (already sorted by updated_at DESC)
            if (!aiAssessmentMap.has(assessment.skill_name)) {
              aiAssessmentMap.set(assessment.skill_name, assessment);
            }
          });
        }
        setAiAssessments(aiAssessmentMap);

        // Fetch public AI video interview (if the VA has published one)
        const publicInterview = await aiInterviewService.getPublicInterviewForVA(profile.id);
        setAiInterview(publicInterview);

        setError(null);

      } catch (err) {
        if (process.env.NODE_ENV === 'development') {
          console.error('[PublicVAProfile] Error fetching public profile:', err);
        }
        setError(err.message || 'Unable to load profile data');
      } finally {
        setIsLoading(false);
      }
    };

  // Data fetching and real-time subscription effect
  useEffect(() => {
    fetchPublicProfile();
  }, [username]);

  // Subscribe to real-time profile updates (for AI assessment skills sync)
  useEffect(() => {
    if (!originalProfileData?.id) return;

    console.log('[PublicVAProfile] Setting up real-time listener for profile changes');
    
    const channel = supabase
      .channel(`public:user_profiles:id=eq.${originalProfileData.id}`)
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'user_profiles',
        filter: `id=eq.${originalProfileData.id}`
      }, (payload) => {
        console.log('[PublicVAProfile] User profile updated, refreshing data:', payload);
        fetchPublicProfile();
      })
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          console.log('[PublicVAProfile] Subscribed to user_profiles changes');
        }
      });

    return () => {
      console.log('[PublicVAProfile] Unsubscribing from user_profiles changes');
      channel.unsubscribe();
    };
  }, [originalProfileData?.id]);

  // Track profile view when profile data is loaded
  useEffect(() => {
    const trackProfileView = async () => {
      // Only track if we have loaded profile data and it's not the user's own profile
      if (profileData && originalProfileData && user?.id !== originalProfileData.id) {
        try {
          await ProfileViewsService.trackProfileView(originalProfileData.id, {
            viewerId: user?.id || null,
            referrer: document.referrer || null,
            userAgent: navigator.userAgent || null
          });
          console.log('Profile view tracked for:', originalProfileData.full_name);
        } catch (error) {
          console.error('Failed to track profile view:', error);
        }
      }
    };

    trackProfileView();
  }, [profileData, originalProfileData, user?.id]);

  // Handle contact/message functionality
  const handleContactVA = () => {
    if (profileData?.id) {
      navigate(`/contact-va/${profileData.id}`);
    }
  };

  const handleShareProfile = () => {
    const profileUrl = `${window.location.origin}/va/${username}`;
    
    if (navigator.share) {
      navigator.share({
        title: `${profileData?.full_name || 'Virtual Assistant'} - Profile`,
        text: profileData?.bio || 'Check out this Virtual Assistant profile',
        url: profileUrl,
      });
    } else {
      navigator.clipboard.writeText(profileUrl);
      setToast({ 
        show: true, 
        message: 'Profile link copied to clipboard!', 
        type: 'success' 
      });
    }
  };

  // Calculate completion percentage for public view (simplified)
  const calculateCompletionPercentage = () => {
    if (!profileData) return 0;
    
    const fields = [
      profileData.full_name,
      profileData.bio,
      profileData.location,
      profileData.hourly_rate,
      profileData.profile_picture,
      profileData.skills && profileData.skills.length > 0,
      profileData.experience_years,
      portfolioItems.length > 0
    ];
    
    const filledFields = fields.filter(Boolean).length;
    return Math.round((filledFields / fields.length) * 100);
  };

  const completionPercentage = calculateCompletionPercentage();

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            <strong className="font-bold">Profile Not Found</strong>
            <p className="block">
              {error === 'Profile not found' 
                ? 'This profile does not exist or is not publicly available.' 
                : 'Unable to load profile data.'
              }
            </p>
          </div>
          <button 
            onClick={() => navigate('/')} 
            className="bg-primary hover:bg-primary-600 text-white font-bold py-2 px-4 rounded"
          >
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  // Main render
  return (
    <div className="min-h-screen bg-gray-50">
      {/* SEO Meta Tags */}
      <ProfileSEO profile={profileData} isPublicView={true} />
      
      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ ...toast, show: false })}
        />
      )}
      
      {/* Mobile Layout - Stacked */}
      <div className="lg:hidden">
        <ProfileHero 
          profile={profileData}
          onEditProfile={null}
          onShareProfile={handleShareProfile}
          onContactVA={handleContactVA}
          completionPercentage={completionPercentage}
          isPublicView={true}
          aiAssessments={aiAssessments}
        />
        
        {/* Mobile Content Sections */}
        <div className="px-4 py-6 space-y-6">
          <AboutSection profile={profileData} isPublicView={true} />
          <VideoIntroductionSection profile={profileData} />
          {(user?.id === profileData?.id || (userProfile?.role === 'employer' && getCurrentPlan()?.tier !== 'free')) ? (
            <AIVideoInterviewSection interview={aiInterview} vaName={profileData?.full_name} />
          ) : (
            <>
              {/* Upgrade Prompt for Non-Logged-In Users */}
              {!user && (
                <div className="bg-gradient-to-r from-primary-50 to-blue-50 border border-primary-300 rounded-lg p-6">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0">
                      <Icon name="Video" size={24} className="text-primary-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-primary-900 mb-2">
                        Watch this VA's AI Interview
                      </h3>
                      <p className="text-primary-800 mb-4">
                        See this VA's AI-powered interview to learn about their skills and experience. Sign in or create an account as an employer to view interviews.
                      </p>
                      <div className="flex flex-wrap gap-3">
                        <button
                          onClick={() => navigate('/login')}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium transition-colors"
                        >
                          <Icon name="LogIn" size={18} />
                          Sign In
                        </button>
                        <button
                          onClick={() => navigate('/register')}
                          className="inline-flex items-center gap-2 px-4 py-2 border border-primary-600 text-primary-600 hover:bg-primary-50 rounded-lg font-medium transition-colors"
                        >
                          <Icon name="UserPlus" size={18} />
                          Sign Up as Employer
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Premium Feature Lock (for free tier employers) */}
              {user && userProfile?.role === 'employer' && getCurrentPlan()?.tier === 'free' && (
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0">
                      <Icon name="Lock" size={24} className="text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-blue-900 mb-2">
                        Premium Feature
                      </h3>
                      <p className="text-blue-800 mb-4">
                        AI interviews are available exclusively to our paid plan subscribers. This helps VAs showcase their skills and experience directly to qualified employers like you.
                      </p>
                      <button
                        onClick={() => navigate('/pricing')}
                        className="inline-flex items-center gap-2 px-4 py-2 text-white rounded-lg font-medium transition-opacity hover:opacity-90"
                        style={{ background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)' }}
                      >
                        <Icon name="Zap" size={18} />
                        Upgrade to Paid Plan
                      </button>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Message for VAs */}
              {user && userProfile?.role === 'va' && (
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0">
                      <Icon name="Info" size={24} className="text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-blue-900 mb-2">
                        AI Interview
                      </h3>
                      <p className="text-blue-800">
                        Only employers can view AI interviews.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
          <WorkStyleSection profile={profileData} isPublicView={true} />
          
          <PrivacySectionWrapper 
            section="skills" 
            profileData={profileData}
            showFallback={true}
            fallbackMessage="Skills information is private"
          >
            <SkillsSection 
              profile={profileData}
              skillAssessments={skillAssessments}
              isPublicView={true}
            />
          </PrivacySectionWrapper>
          
          <PrivacySectionWrapper 
            section="experience" 
            profileData={profileData}
            showFallback={true}
            fallbackMessage="Work experience is private"
          >
            <ExperienceTimeline profile={profileData} isPublicView={true} />
          </PrivacySectionWrapper>
          
          <PrivacySectionWrapper 
            section="portfolio" 
            profileData={profileData}
            showFallback={true}
            fallbackMessage="Portfolio is private"
          >
            <PortfolioGallery 
              profile={profileData}
              portfolioItems={portfolioItems}
              isPublicView={true}
            />
          </PrivacySectionWrapper>
          
          {profileData?.reviewCount > 0 && (
            <ReviewsSection 
              profile={profileData}
              reviews={reviews}
              isPublicView={true}
            />
          )}
        </div>
      </div>

      {/* Desktop Resume-Style Layout */}
      <div className="hidden lg:block">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex gap-6">
            {/* Left Sidebar - Profile Hero */}
            <aside className="w-80 flex-shrink-0 sticky top-20 self-start">
              <ProfileHeroSidebar 
                profile={profileData}
                onEditProfile={null}
                onShareProfile={handleShareProfile}
                completionPercentage={completionPercentage}
                isPublicView={true}
                aiAssessments={aiAssessments}
              />
            </aside>

            {/* Main Content Area - Scrollable */}
            <main className="flex-1 min-w-0">
              <div className="space-y-6">
                <AboutSection profile={profileData} isPublicView={true} />
                {(user?.id === profileData?.id || (userProfile?.role === 'employer' && getCurrentPlan()?.tier !== 'free')) ? (
                  <>
                    <VideoIntroductionSection profile={profileData} />
                    <AIVideoInterviewSection interview={aiInterview} vaName={profileData?.full_name} />
                  </>
                ) : (
                  <>
                    {/* Upgrade Prompt for Non-Logged-In Users */}
                    {!user && (
                      <div className="bg-gradient-to-r from-primary-50 to-blue-50 border border-primary-300 rounded-lg p-6">
                        <div className="flex items-start gap-4">
                          <div className="flex-shrink-0">
                            <Icon name="Video" size={24} className="text-primary-600" />
                          </div>
                          <div className="flex-1">
                            <h3 className="text-lg font-semibold text-primary-900 mb-2">
                              Watch this VA's AI Interview.
                            </h3>
                            <p className="text-primary-800 mb-4">
                              Candidate has taken an AI-powered interview. Sign in or create an account as an employer to view interviews.
                            </p>
                            <div className="flex flex-wrap gap-3">
                              <button
                                onClick={() => navigate('/login')}
                                className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium transition-colors"
                              >
                                <Icon name="LogIn" size={18} />
                                Sign In
                              </button>
                              <button
                                onClick={() => navigate('/register')}
                                className="inline-flex items-center gap-2 px-4 py-2 border border-primary-600 text-primary-600 hover:bg-primary-50 rounded-lg font-medium transition-colors"
                              >
                                <Icon name="UserPlus" size={18} />
                                Sign Up as Employer
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Premium Feature Lock (for free tier employers) */}
                    {user && userProfile?.role === 'employer' && getCurrentPlan()?.tier === 'free' && (
                      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6">
                        <div className="flex items-start gap-4">
                          <div className="flex-shrink-0">
                            <Icon name="Lock" size={24} className="text-blue-600" />
                          </div>
                          <div className="flex-1">
                            <h3 className="text-lg font-semibold text-blue-900 mb-2">
                              Unlock Premium Feature
                            </h3>
                            <p className="text-blue-800 mb-4">
                              AI interviews and Video Introductions are available exclusively to our paid plan subscribers. This helps VAs showcase their skills and experience directly to qualified employers like you.
                            </p>
                            <button
                              onClick={() => navigate('/pricing')}
                              className="inline-flex items-center gap-2 px-4 py-2 text-white rounded-lg font-medium transition-opacity hover:opacity-90"
                              style={{ background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)' }}
                            >
                              <Icon name="Zap" size={18} />
                              Upgrade to Paid Plan
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Message for VAs */}
                    {user && userProfile?.role === 'va' && (
                      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6">
                        <div className="flex items-start gap-4">
                          <div className="flex-shrink-0">
                            <Icon name="Info" size={24} className="text-blue-600" />
                          </div>
                          <div className="flex-1">
                            <h3 className="text-lg font-semibold text-blue-900 mb-2">
                              Candidate has taken an AI Interview.
                            </h3>
                            <p className="text-blue-800">
                              Only employers can view AI interviews.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
                <WorkStyleSection profile={profileData} isPublicView={true} />
                
                <PrivacySectionWrapper 
                  section="skills" 
                  profileData={profileData}
                  showFallback={true}
                  fallbackMessage="Skills information is private"
                >
                  <SkillsSection 
                    profile={profileData}
                    skillAssessments={skillAssessments}
                    isPublicView={true}
                  />
                </PrivacySectionWrapper>
                
                <PrivacySectionWrapper 
                  section="experience" 
                  profileData={profileData}
                  showFallback={true}
                  fallbackMessage="Work experience is private"
                >
                  <ExperienceTimeline profile={profileData} isPublicView={true} />
                </PrivacySectionWrapper>
                
                <PrivacySectionWrapper 
                  section="portfolio" 
                  profileData={profileData}
                  showFallback={true}
                  fallbackMessage="Portfolio is private"
                >
                  <PortfolioGallery 
                    profile={profileData}
                    portfolioItems={portfolioItems}
                    isPublicView={true}
                  />
                </PrivacySectionWrapper>
                
                {profileData?.reviewCount > 0 && (
                  <ReviewsSection 
                    profile={profileData}
                    reviews={reviews}
                    isPublicView={true}
                  />
                )}
              </div>
            </main>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicVAProfile;
